import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SecurityProvider, useSecurity } from './context/SecurityContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import { SoundSettingsProvider } from './context/SoundSettingsContext';
import Loading from './components/Loading';
import LoadingSkeleton from './components/LoadingSkeleton';
import LockScreen from './components/LockScreen';
import ErrorBoundary from './components/ErrorBoundary';
import { debugAdminCheck } from './utils/adminUtils';
import { verifyAdmin } from './services/adminService';

// Screens — lazy loaded for code splitting
const LoginScreen = React.lazy(() => import('./screens/LoginScreen'));
const RegisterScreen = React.lazy(() => import('./screens/RegisterScreen'));
const DashboardScreen = React.lazy(() => import('./screens/DashboardScreen'));
const HomeScreen = React.lazy(() => import('./screens/HomeScreen'));
const ProfileScreen = React.lazy(() => import('./screens/ProfileScreen'));
const StolenCarsScreen = React.lazy(() => import('./screens/StolenCarsScreen'));
const ReportStolenScreen = React.lazy(() => import('./screens/ReportStolenScreen'));
const ReportMotoScreen = React.lazy(() => import('./screens/ReportMotoScreen'));
const SupportScreen = React.lazy(() => import('./screens/SupportScreen'));
const EmailVerificationScreen = React.lazy(() => import('./screens/EmailVerificationScreen'));
const CreatePasswordScreen = React.lazy(() => import('./screens/CreatePasswordScreen'));
const AdminScreen = React.lazy(() => import('./screens/AdminScreen'));
const CpfPendingScreen = React.lazy(() => import('./screens/CpfPendingScreen'));
const PublicSightingsScreen = React.lazy(() => import('./screens/PublicSightingsScreen'));

// Public landing screens
const LandingPageScreen = React.lazy(() => import('./screens/LandingPageScreen'));
const HowItWorksScreen = React.lazy(() => import('./screens/HowItWorksScreen'));
const BlogScreen = React.lazy(() => import('./screens/BlogScreen'));
const PrivacyPolicyScreen = React.lazy(() => import('./screens/PrivacyPolicyScreen'));
const TermsOfUseScreen = React.lazy(() => import('./screens/TermsOfUseScreen'));

/** Wrapper for routes that require authentication */
const ProtectedRoute = ({ children }) => {
  const { user, loading, cpfVerified } = useAuth();
  const { locked, unlock } = useSecurity();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.emailVerified) return <Navigate to="/verify-email" replace />;
  if (user.phoneNumber && !user.hasPasswordProvider) return <Navigate to="/create-password" replace />;
  if (!cpfVerified) return <Navigate to="/verify-cpf" replace />;
  if (locked) return <LockScreen onUnlock={unlock} />;
  return children;
};

/** Wrapper for admin-only routes */
const AdminRoute = ({ children }) => {
  const { user, loading, cpfVerified, token } = useAuth();
  const { locked, unlock } = useSecurity();
  const [isAdminVerified, setIsAdminVerified] = React.useState(null);

  React.useEffect(() => {
    if (!user || !token) {
      setIsAdminVerified(false);
      return;
    }
    let cancelled = false;
    verifyAdmin(token).then((result) => {
      if (!cancelled) {
        setIsAdminVerified(result);
        if (result && user?.uid) {
          try { localStorage.setItem(`@car_alert:admin_${user.uid}`, 'true'); } catch (_) {}
        }
      }
    });
    return () => { cancelled = true; };
  }, [user, token]);

  if (loading || cpfVerified === null || isAdminVerified === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.emailVerified) return <Navigate to="/verify-email" replace />;
  if (!cpfVerified) return <Navigate to="/verify-cpf" replace />;
  if (locked) return <LockScreen onUnlock={unlock} />;
  if (!isAdminVerified) {
    debugAdminCheck(user.uid);
    if (process.env.NODE_ENV === 'development') {
      console.warn('[AdminRoute] Access denied for UID:', user.uid);
    }
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

/** Wrapper for routes only accessible when logged out */
const PublicRoute = ({ children }) => {
  const { user, loading, cpfVerified } = useAuth();
  if (loading) return <Loading />;
  if (!user) return children;
  // A phone-authenticated user who has not yet linked an email address is still
  // completing the SMS registration flow (sms_email_form step). Allow them to
  // remain on public routes (e.g. /register) so they can finish setting up
  // their email and password without being prematurely redirected.
  if (!user.emailVerified && !user.email) return children;
  if (!user.emailVerified) return <Navigate to="/verify-email" replace />;
  if (user.phoneNumber && !user.hasPasswordProvider) return <Navigate to="/create-password" replace />;
  if (!cpfVerified) return <Navigate to="/verify-cpf" replace />;
  return <Navigate to="/dashboard" replace />;
};

/** Route only accessible when logged in but email not yet verified */
const VerifyEmailRoute = ({ children }) => {
  const { user, loading, cpfVerified } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.emailVerified) {
    // Phone-authenticated users who have not yet created a password go to /create-password
    if (user.phoneNumber && !user.hasPasswordProvider) return <Navigate to="/create-password" replace />;
    return <Navigate to={cpfVerified ? '/dashboard' : '/verify-cpf'} replace />;
  }
  return children;
};

/** Route only accessible when logged in, email verified, but CPF not yet registered */
const CpfPendingRoute = ({ children }) => {
  const { user, loading, cpfVerified } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.emailVerified) return <Navigate to="/verify-email" replace />;
  if (cpfVerified) return <Navigate to="/dashboard" replace />;
  return children;
};

/** Route only accessible when logged in, email verified, but password provider not yet linked */
const CreatePasswordRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.emailVerified) return <Navigate to="/verify-email" replace />;
  if (user.hasPasswordProvider) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  return (
    <React.Suspense fallback={<LoadingSkeleton />}>
      <Routes>
        {/* Public landing routes — accessible without authentication */}
        <Route path="/" element={<LandingPageScreen />} />
        <Route path="/how-it-works" element={<HowItWorksScreen />} />
        <Route path="/blog" element={<BlogScreen />} />
        <Route path="/blog/:slug" element={<BlogScreen />} />
        <Route path="/privacy" element={<PrivacyPolicyScreen />} />
        <Route path="/privacidade" element={<PrivacyPolicyScreen />} />
        <Route path="/terms" element={<TermsOfUseScreen />} />
        <Route path="/termos" element={<TermsOfUseScreen />} />

        <Route
          path="/login"
          element={<PublicRoute><LoginScreen /></PublicRoute>}
        />
        <Route
          path="/register"
          element={<PublicRoute><RegisterScreen /></PublicRoute>}
        />
        <Route
          path="/verify-email"
          element={<VerifyEmailRoute><EmailVerificationScreen /></VerifyEmailRoute>}
        />
        <Route
          path="/verify-cpf"
          element={<CpfPendingRoute><CpfPendingScreen /></CpfPendingRoute>}
        />
        <Route
          path="/create-password"
          element={<CreatePasswordRoute><CreatePasswordScreen /></CreatePasswordRoute>}
        />
        <Route
          path="/dashboard"
          element={<ProtectedRoute><DashboardScreen /></ProtectedRoute>}
        />
        <Route
          path="/home"
          element={<ProtectedRoute><HomeScreen /></ProtectedRoute>}
        />
        <Route
          path="/profile"
          element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>}
        />
        <Route
          path="/stolen-cars"
          element={<ProtectedRoute><StolenCarsScreen /></ProtectedRoute>}
        />
        <Route
          path="/report"
          element={<ProtectedRoute><ReportStolenScreen /></ProtectedRoute>}
        />
        <Route
          path="/cadastrar-moto"
          element={<ProtectedRoute><ReportMotoScreen /></ProtectedRoute>}
        />
        <Route
          path="/support"
          element={<ProtectedRoute><SupportScreen /></ProtectedRoute>}
        />
        <Route
          path="/sightings"
          element={<ProtectedRoute><PublicSightingsScreen /></ProtectedRoute>}
        />
        <Route
          path="/admin"
          element={<AdminRoute><AdminScreen /></AdminRoute>}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <SoundSettingsProvider>
            <BrowserRouter>
              <SecurityProvider>
                <ToastProvider>
                  <AppRoutes />
                </ToastProvider>
              </SecurityProvider>
            </BrowserRouter>
          </SoundSettingsProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
